import React, { useState, useEffect } from 'react';
import {
    Save, Settings, Activity, Shield, Truck, Bell, Database,
    RefreshCw, Download, Upload, ExternalLink, Map, MessageSquare,
    Mail, Smartphone, HardDrive, FolderOpen, CreditCard, FileText, Eye
} from 'lucide-react';
import ConfigSection from '../components/configuration/ConfigSection';
import ConfigInput from '../components/configuration/ConfigInput';
import ConfigToggle from '../components/configuration/ConfigToggle';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { configService } from '../services/config.service';
import { formatDateForFileName } from '../utils/dateUtils';
import { setMoneyDefaults } from '../utils/formatMoney';
import { API_BASE_URL } from '../utils/constants';
import { useDialog } from '../contexts/DialogContext';

const Configuration = () => {
    const { confirm } = useDialog();
    const [config, setConfig] = useState(null);
    const [originalConfig, setOriginalConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [uploadingPolicy, setUploadingPolicy] = useState(false);
    const [uploadingVendorPolicy, setUploadingVendorPolicy] = useState(false);

    useEffect(() => {
        fetchConfiguration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Check if there are any changes
        if (originalConfig && config) {
            const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
            setHasChanges(changed);
        }
    }, [config, originalConfig]);

    const fetchConfiguration = async () => {
        try {
            setLoading(true);
            const data = await configService.getConfiguration();
            setConfig(data);
            setOriginalConfig(JSON.parse(JSON.stringify(data))); // Deep copy
            const payments = data?.payments || {};
            setMoneyDefaults({
                currency: payments.default_currency?.value,
                locale: payments.default_locale?.value,
                timezone: payments.default_timezone?.value,
            });
        } catch (error) {
            showMessage('Failed to load configuration: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (section, key, value) => {
        setConfig(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: {
                    ...prev[section][key],
                    value: value
                }
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');

        try {
            await configService.updateConfiguration(config);
            setOriginalConfig(JSON.parse(JSON.stringify(config))); // Update original after save
            const payments = config?.payments || {};
            setMoneyDefaults({
                currency: payments.default_currency?.value,
                locale: payments.default_locale?.value,
                timezone: payments.default_timezone?.value,
            });
            showMessage('Configuration saved successfully!', 'success');
            setHasChanges(false);
        } catch (error) {
            showMessage('Failed to save configuration: ' + error.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        const ok = await confirm({
            title: 'Discard changes',
            message: 'Are you sure you want to discard all changes?',
            confirmLabel: 'Discard',
            danger: false,
        });
        if (ok) {
            setConfig(JSON.parse(JSON.stringify(originalConfig)));
            showMessage('Changes discarded', 'info');
        }
    };

    const handleExport = async () => {
        try {
            const data = await configService.exportConfiguration();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vaa-config-${formatDateForFileName(new Date())}.json`;
            a.click();
            window.URL.revokeObjectURL(url);
            showMessage('Configuration exported successfully', 'success');
        } catch (error) {
            showMessage('Failed to export configuration: ' + error.message, 'error');
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const importedConfig = JSON.parse(text);

            const ok = await confirm({
                title: 'Import configuration',
                message: 'This will overwrite your current configuration. Continue?',
                confirmLabel: 'Import',
                danger: false,
            });
            if (ok) {
                await configService.importConfiguration(importedConfig);
                await fetchConfiguration();
                showMessage('Configuration imported successfully', 'success');
            }
        } catch (error) {
            showMessage('Failed to import configuration: ' + error.message, 'error');
        }
    };

    const handlePrivacyPolicyUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setUploadingPolicy(true);
        try {
            await configService.uploadPrivacyPolicyFile(file);
            await fetchConfiguration();
            showMessage('Privacy policy file uploaded. Customers will see this document at signup.', 'success');
        } catch (error) {
            showMessage('Failed to upload privacy policy: ' + error.message, 'error');
        } finally {
            setUploadingPolicy(false);
        }
    };

    const handlePrivacyPolicyFileClear = async () => {
        const ok = await confirm({
            title: 'Remove privacy policy',
            message: 'Remove the uploaded privacy policy file?',
            confirmLabel: 'Remove',
        });
        if (!ok) return;
        setUploadingPolicy(true);
        try {
            await configService.deletePrivacyPolicyFile();
            await fetchConfiguration();
            showMessage('Uploaded policy file removed.', 'success');
        } catch (error) {
            showMessage('Failed to remove file: ' + error.message, 'error');
        } finally {
            setUploadingPolicy(false);
        }
    };

    const handleVendorPrivacyPolicyUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setUploadingVendorPolicy(true);
        try {
            await configService.uploadVendorPrivacyPolicyFile(file);
            await fetchConfiguration();
            showMessage('Vendor privacy policy file uploaded. Vendors will see this document at login.', 'success');
        } catch (error) {
            showMessage('Failed to upload vendor privacy policy: ' + error.message, 'error');
        } finally {
            setUploadingVendorPolicy(false);
        }
    };

    const handleVendorPrivacyPolicyFileClear = async () => {
        const ok = await confirm({
            title: 'Remove vendor privacy policy',
            message: 'Remove the uploaded vendor privacy policy file?',
            confirmLabel: 'Remove',
        });
        if (!ok) return;
        setUploadingVendorPolicy(true);
        try {
            await configService.deleteVendorPrivacyPolicyFile();
            await fetchConfiguration();
            showMessage('Uploaded vendor policy file removed.', 'success');
        } catch (error) {
            showMessage('Failed to remove vendor file: ' + error.message, 'error');
        } finally {
            setUploadingVendorPolicy(false);
        }
    };

    const showMessage = (msg, type) => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => {
            setMessage('');
            setMessageType('');
        }, 5000);
    };

    // Check if a field should be visible based on dependencies / ui_hidden
    const isFieldVisible = (field, section) => {
        if (field?.ui_hidden === true || field?.hidden === true) return false;
        if (!field.depends_on) return true;

        const dependsOnSection = field.depends_on.section || section;
        const dependsOnField = config[dependsOnSection]?.[field.depends_on.field];
        if (!dependsOnField) return true;

        return dependsOnField.value === field.depends_on.value;
    };

    const renderField = (key, field, section) => {
        if (!isFieldVisible(field, section)) return null;

        if (field.type === 'boolean') {
            return (
                <ConfigToggle
                    key={key}
                    label={field.label}
                    value={field.value}
                    onChange={(v) => handleChange(section, key, v)}
                    description={field.description}
                />
            );
        } else if (field.type === 'select') {
            return (
                <div className='regit_bform ' key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <select
                        value={field.value}
                        onChange={(e) => handleChange(section, key, e.target.value)}
                        className="outline-none w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        {field.options?.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    {field.description && (
                        <p className="mt-1 text-sm text-gray-500">{field.description}</p>
                    )}
                </div>
            );
        } else {
            // Determine suffix based on key
            let suffix = undefined;
            if (key.includes('timeout') || key.includes('seconds') || key.includes('expiry_seconds')) {
                suffix = 'seconds';
            } else if (key.includes('minutes')) {
                suffix = 'minutes';
            } else if (key.includes('hours')) {
                suffix = 'hours';
            } else if (key.includes('days')) {
                suffix = 'days';
            } else if (key.includes('_km')) {
                suffix = 'km';
            } else if (key.includes('meters')) {
                suffix = 'meters';
            } else if (key.includes('rate') && key.includes('limit')) {
                suffix = 'req/min';
            } else if (key.includes('size_mb')) {
                suffix = 'MB';
            } else if (key.includes('kmh')) {
                suffix = 'km/h';
            }

            return (
                <ConfigInput
                    key={key}
                    label={field.label}
                    value={field.value}
                    onChange={(v) => handleChange(section, key, field.type === 'number' ? parseFloat(v) : v)}
                    type={field.type}
                    description={field.description}
                    min={field.min}
                    max={field.max}
                    suffix={suffix}
                />
            );
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!config) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Failed to load configuration</p>
                <button
                    onClick={fetchConfiguration}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white flex justify-between items-center bxshadowhd p-3 mrgall border">
                <h1 className="text-2xl font-semibold text-gray-900">Configuration</h1>
                <div className="flex items-center gap-3">
                    <input
                        type="file"
                        id="import-config"
                        accept=".json"
                        onChange={handleImport}
                        className="hidden"
                    />
                    <label
                        htmlFor="import-config"
                        className="flex items-center gap-2 px-4 py-2 border border-blue-700 text-blue-700 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                        <Upload className="w-4 h-4" />
                        Import
                    </label>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-500 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                    {hasChanges && (
                        <button
                            onClick={handleReset}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-lg hover:bg-scarlet-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-lg ${messageType === 'success' ? 'bg-green-50 text-green-700' :
                    messageType === 'error' ? 'bg-red-50 text-red-700' :
                        'bg-blue-50 text-blue-700'
                    }`}>
                    {message}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mrgall">
                {/* System Settings */}
                {config.system && (
                    <ConfigSection title="System Settings" icon={Settings}>
                        {Object.entries(config.system).map(([key, field]) => renderField(key, field, 'system'))}
                    </ConfigSection>
                )}

                {/* API Settings */}
                {config.api && (
                    <ConfigSection title="API Settings" icon={Activity}>
                        {Object.entries(config.api).map(([key, field]) => renderField(key, field, 'api'))}
                    </ConfigSection>
                )}

                {/* Authentication Settings */}
                {config.authentication && (
                    <ConfigSection title="Authentication" icon={Shield}>
                        {Object.entries(config.authentication).map(([key, field]) => renderField(key, field, 'authentication'))}
                    </ConfigSection>
                )}

                {/* Service Settings */}
                {config.service && (
                    <ConfigSection title="Service Settings" icon={Truck}>
                        {Object.entries(config.service).map(([key, field]) => renderField(key, field, 'service'))}
                    </ConfigSection>
                )}

                {/* Notification Settings */}
                {config.notification && (
                    <ConfigSection title="Notifications" icon={Bell}>
                        {Object.entries(config.notification).map(([key, field]) => renderField(key, field, 'notification'))}
                    </ConfigSection>
                )}

                {/* Database Settings */}
                {config.database && (
                    <ConfigSection title="Database" icon={Database}>
                        {Object.entries(config.database).map(([key, field]) => renderField(key, field, 'database'))}
                    </ConfigSection>
                )}

                {/* Payment Settings */}
                {config.payments && (
                    <ConfigSection title="Payments" icon={Activity}>
                        {Object.entries(config.payments).map(([key, field]) => renderField(key, field, 'payments'))}
                    </ConfigSection>
                )}

                {/* Razorpay Settings */}
                {config.razorpay && Object.entries(config.razorpay).some(([, field]) => isFieldVisible(field, 'razorpay')) && (
                    <ConfigSection title="Razorpay" icon={Activity}>
                        {Object.entries(config.razorpay).map(([key, field]) => renderField(key, field, 'razorpay'))}
                    </ConfigSection>
                )}

                {/* Stripe Settings */}
                {config.stripe && Object.entries(config.stripe).some(([, field]) => isFieldVisible(field, 'stripe')) && (
                    <ConfigSection title="Stripe" icon={Activity}>
                        {Object.entries(config.stripe).map(([key, field]) => renderField(key, field, 'stripe'))}
                    </ConfigSection>
                )}

                {/* PayU Settings */}
                {config.payu && Object.entries(config.payu).some(([, field]) => isFieldVisible(field, 'payu')) && (
                    <ConfigSection title="PayU" icon={Activity}>
                        {Object.entries(config.payu).map(([key, field]) => renderField(key, field, 'payu'))}
                    </ConfigSection>
                )}
                {/* Business Rules */}
                {config.business_rules && (
                    <ConfigSection title="Business Rules" icon={Activity}>
                        {Object.entries(config.business_rules).map(([key, field]) => renderField(key, field, 'business_rules'))}
                    </ConfigSection>
                )}

                {/* Object Storage (S3-compatible) - Full Width */}
                {config.s3 && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Object Storage (S3-compatible)" icon={HardDrive}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.s3).map(([key, field]) => (
                                    <div key={key} className={
                                        ['enabled', 'auto_create_buckets', 'enable_versioning', 'enable_lifecycle'].includes(key)
                                            ? 'lg:col-span-2' : ''
                                    }>
                                        {renderField(key, field, 's3')}
                                    </div>
                                ))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* Storage Buckets - Show when object storage is enabled */}
                {config.s3_buckets && config.s3?.enabled?.value && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Storage Buckets" icon={FolderOpen}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.s3_buckets).map(([key, field]) => renderField(key, field, 's3_buckets'))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* Google Maps - Full Width */}
                {config.google_maps && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Google Maps Integration" icon={Map}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.google_maps).map(([key, field]) => (
                                    <div key={key} className={key === 'google_maps_enabled' ? 'lg:col-span-2' : ''}>
                                        {renderField(key, field, 'google_maps')}
                                    </div>
                                ))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* FCM Settings - Show when FCM is enabled */}
                {config.fcm_settings && config.notification?.fcm_enabled?.value && (
                    <ConfigSection title="FCM Configuration" icon={Smartphone}>
                        {Object.entries(config.fcm_settings).map(([key, field]) => renderField(key, field, 'fcm_settings'))}
                    </ConfigSection>
                )}

                {/* SMS Settings - Show when SMS is enabled */}
                {config.sms_settings && config.notification?.sms_enabled?.value && (
                    <ConfigSection title="SMS Configuration" icon={MessageSquare}>
                        {Object.entries(config.sms_settings).map(([key, field]) => renderField(key, field, 'sms_settings'))}
                    </ConfigSection>
                )}

                {/* Email Settings - Show when Email is enabled */}
                {config.email_settings && config.notification?.email_enabled?.value && (
                    <ConfigSection title="Email Configuration" icon={Mail}>
                        {Object.entries(config.email_settings).map(([key, field]) => renderField(key, field, 'email_settings'))}
                    </ConfigSection>
                )}

                {/* External Services - Full Width */}
                {config.external_services && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="External Services" icon={ExternalLink}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.external_services).map(([key, field]) => (
                                    <div key={key} className={key === 'rsa_integration_enabled' ? 'lg:col-span-2' : ''}>
                                        {renderField(key, field, 'external_services')}
                                    </div>
                                ))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* Customer Payments - Full Width */}
                {config.payments && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Customer Payments" icon={CreditCard}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.payments).map(([key, field]) => (
                                    <div key={key} className={key === 'payment_provider' ? 'lg:col-span-2' : ''}>
                                        {renderField(key, field, 'payments')}
                                    </div>
                                ))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* Stripe — only when payment_provider === stripe (handled by depends_on) */}
                {config.stripe && config.payments?.payment_provider?.value === 'stripe' && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Stripe" icon={CreditCard}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.stripe).map(([key, field]) => renderField(key, field, 'stripe'))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* Razorpay (customer-side) — only when payment_provider === razorpay */}
                {config.razorpay && config.payments?.payment_provider?.value === 'razorpay' && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Razorpay (Customer Checkout)" icon={CreditCard}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.razorpay).map(([key, field]) => renderField(key, field, 'razorpay'))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {/* PayU — only when payment_provider === payu */}
                {config.payu && config.payments?.payment_provider?.value === 'payu' && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="PayU" icon={CreditCard}>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(config.payu).map(([key, field]) => renderField(key, field, 'payu'))}
                            </div>
                        </ConfigSection>
                    </div>
                )}

                {config.legal && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Privacy Policy (Customer App)" icon={FileText}>
                            <p className="text-sm text-gray-500 mb-4">
                                Upload a PDF or Word document shown when a customer first logs in.
                                Bump the version when you replace the file so existing customers must accept again.
                            </p>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                {['privacy_policy_version', 'privacy_policy_updated_at']
                                    .filter((key) => config.legal[key])
                                    .map((key) => (
                                        <div key={key}>
                                            {renderField(key, config.legal[key], 'legal')}
                                        </div>
                                    ))}
                            </div>
                            {(() => {
                                const fileName = config.legal.privacy_policy_file_name?.value || '';
                                const ext = (fileName.split('.').pop() || '').toLowerCase();
                                const isPdf = ext === 'pdf';
                                const cache = encodeURIComponent(fileName || String(Date.now()));
                                const previewUrl = `${API_BASE_URL}/api/auth/customer/privacy-policy/file?t=${cache}`;
                                const downloadUrl = `${API_BASE_URL}/api/auth/customer/privacy-policy/file?download=true`;
                                return (
                                    <div className="p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                                        <p className="text-sm font-medium text-gray-800 mb-1">Policy file</p>
                                        <p className="text-xs text-gray-500 mb-3">
                                            PDF or Word (.pdf, .doc, .docx). Max 10 MB.
                                        </p>
                                        <div className="flex flex-wrap items-center gap-3 mb-4">
                                            <input
                                                type="file"
                                                id="privacy-policy-file"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={handlePrivacyPolicyUpload}
                                                className="hidden"
                                                disabled={uploadingPolicy}
                                            />
                                            <label
                                                htmlFor="privacy-policy-file"
                                                className={`flex items-center gap-2 px-4 py-2 border border-blue-700 text-blue-700 rounded-lg hover:bg-white cursor-pointer transition-colors ${uploadingPolicy ? 'opacity-50 pointer-events-none' : ''}`}
                                            >
                                                <Upload className="w-4 h-4" />
                                                {uploadingPolicy ? 'Uploading…' : fileName ? 'Replace file' : 'Upload file'}
                                            </label>
                                            {fileName ? (
                                                <>
                                                    <a
                                                        href={previewUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Preview
                                                    </a>
                                                    <a
                                                        href={downloadUrl}
                                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        Download
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={handlePrivacyPolicyFileClear}
                                                        disabled={uploadingPolicy}
                                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                                                    >
                                                        Remove
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                        {fileName ? (
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="px-3 py-2 border-b border-gray-200 text-sm text-gray-700 flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-500" />
                                                    <span className="font-medium truncate">{fileName}</span>
                                                </div>
                                                {isPdf ? (
                                                    <iframe
                                                        title="Privacy policy preview"
                                                        src={previewUrl}
                                                        className="w-full h-[520px] bg-white"
                                                    />
                                                ) : (
                                                    <div className="p-6 text-sm text-gray-600">
                                                        Word documents cannot be previewed in the browser. Use Preview to open
                                                        the file, or Download to save a copy.
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No file uploaded yet. Apps fall back to the built-in sample PDF until you upload one.</p>
                                        )}
                                    </div>
                                );
                            })()}
                        </ConfigSection>
                    </div>
                )}

                {config.legal && (
                    <div className="lg:col-span-2">
                        <ConfigSection title="Privacy Policy (Vendor App)" icon={FileText}>
                            <p className="text-sm text-gray-500 mb-4">
                                Separate document shown when a vendor or driver logs in.
                                Bump the version when you replace the file so existing users must accept again.
                            </p>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                {['vendor_privacy_policy_version', 'vendor_privacy_policy_updated_at']
                                    .filter((key) => config.legal[key])
                                    .map((key) => (
                                        <div key={key}>
                                            {renderField(key, config.legal[key], 'legal')}
                                        </div>
                                    ))}
                            </div>
                            {(() => {
                                const fileName = config.legal.vendor_privacy_policy_file_name?.value || '';
                                const ext = (fileName.split('.').pop() || '').toLowerCase();
                                const isPdf = ext === 'pdf';
                                const cache = encodeURIComponent(fileName || String(Date.now()));
                                const previewUrl = `${API_BASE_URL}/api/auth/vendor/privacy-policy/file?t=${cache}`;
                                const downloadUrl = `${API_BASE_URL}/api/auth/vendor/privacy-policy/file?download=true`;
                                return (
                                    <div className="p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                                        <p className="text-sm font-medium text-gray-800 mb-1">Vendor policy file</p>
                                        <p className="text-xs text-gray-500 mb-3">
                                            PDF or Word (.pdf, .doc, .docx). Max 10 MB.
                                        </p>
                                        <div className="flex flex-wrap items-center gap-3 mb-4">
                                            <input
                                                type="file"
                                                id="vendor-privacy-policy-file"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={handleVendorPrivacyPolicyUpload}
                                                className="hidden"
                                                disabled={uploadingVendorPolicy}
                                            />
                                            <label
                                                htmlFor="vendor-privacy-policy-file"
                                                className={`flex items-center gap-2 px-4 py-2 border border-blue-700 text-blue-700 rounded-lg hover:bg-white cursor-pointer transition-colors ${uploadingVendorPolicy ? 'opacity-50 pointer-events-none' : ''}`}
                                            >
                                                <Upload className="w-4 h-4" />
                                                {uploadingVendorPolicy ? 'Uploading…' : fileName ? 'Replace file' : 'Upload file'}
                                            </label>
                                            {fileName ? (
                                                <>
                                                    <a
                                                        href={previewUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Preview
                                                    </a>
                                                    <a
                                                        href={downloadUrl}
                                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        Download
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={handleVendorPrivacyPolicyFileClear}
                                                        disabled={uploadingVendorPolicy}
                                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                                                    >
                                                        Remove
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                        {fileName ? (
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="px-3 py-2 border-b border-gray-200 text-sm text-gray-700 flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-500" />
                                                    <span className="font-medium truncate">{fileName}</span>
                                                </div>
                                                {isPdf ? (
                                                    <iframe
                                                        title="Vendor privacy policy preview"
                                                        src={previewUrl}
                                                        className="w-full h-[520px] bg-white"
                                                    />
                                                ) : (
                                                    <div className="p-6 text-sm text-gray-600">
                                                        Word documents cannot be previewed in the browser. Use Preview to open
                                                        the file, or Download to save a copy.
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">No file uploaded yet. Apps fall back to the built-in sample PDF until you upload one.</p>
                                        )}
                                    </div>
                                );
                            })()}
                        </ConfigSection>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Configuration;   